if (!customElements.get("vehicle-make-fitment-explorer")) {
  class VehicleMakeFitmentExplorer extends HTMLElement {
    constructor() {
      super();

      this.fitmentParam = "filter.p.m.custom.vehicle_fitment";
      this.selectionEvent = "vehicle-make-fitment:selection-change";
      this.makeId = this.dataset.makeId || "";
      this.makeGid = this.dataset.makeGid || "";
      this.makeName = this.dataset.makeName || "";
      this.pendingSelectionKey = "vehicle_fitment_pending_selection";
      this.activeFitmentActionLabel = this.translate("active_vehicle");
      this.viewKitsActionLabel = this.translate("view_kits");
      this.selects = Array.from(this.querySelectorAll("select[data-label]"));
      this.status = this.querySelector("[data-fitment-explorer-status]");
      this.results = this.querySelector(".fitment-card-results");
      this.resultHeading = this.querySelector(".fitment-card-results-heading");
      this.cardList = this.querySelector(".fitment-card-list");
      this.actionDock = this.querySelector(".fitment-action-dock");
      this.actionDockStatus = this.actionDock?.querySelector(".fitment-action-dock__status") || null;
      this.actionDockActions = this.actionDock?.querySelector(".fitment-action-dock__actions") || null;
      this.selectedFitment = null;
      this.actionDockPrompt = this.translate("select_vehicle_to_continue");

      this.ensureActionDock();
      this.setupEventListeners();
      this.setStatus(this.translate("loading_fitments"));
      this.whenFitmentIndexReady().then(() => this.initialize());
    }

    setupEventListeners() {
      this.selects.forEach((select) => {
        select.addEventListener("change", (event) => this.onSelectChange(event));
      });

      window.addEventListener("vehicle-fitment-index:ready", () => this.initialize());

      this.cardList?.setAttribute("role", "radiogroup");
      this.cardList?.setAttribute("aria-label", this.translate("vehicle_fitment_options"));
    }

    translate(key, replacements = {}) {
      return window.vehicleFitmentText?.(key, replacements) || key;
    }

    ensureActionDock() {
      if (!this.results) {
        return;
      }

      if (!this.actionDock) {
        this.actionDock = document.createElement("div");
        this.actionDock.className = "fitment-action-dock";
        this.actionDock.hidden = true;
        this.actionDock.setAttribute("aria-live", "polite");

        this.actionDockStatus = document.createElement("div");
        this.actionDockStatus.className = "fitment-action-dock__status";

        this.actionDockActions = document.createElement("div");
        this.actionDockActions.className = "fitment-action-dock__actions";
        this.actionDockActions.hidden = true;

        this.actionDock.append(this.actionDockStatus, this.actionDockActions);
        this.results.append(this.actionDock);
      }

      if (!this.actionDockStatus) {
        this.actionDockStatus = this.actionDock.querySelector(".fitment-action-dock__status");
      }

      if (!this.actionDockActions) {
        this.actionDockActions = this.actionDock.querySelector(".fitment-action-dock__actions");
      }
    }

    initialize() {
      const indexStatus = window.vehicleFitmentIndexStatus || {};
      if (indexStatus.error && !this.makeFitments.length) {
        this.setStatus(this.translate("fitments_unavailable"), true);
        return;
      }

      if (!this.makeFitments.length) {
        this.setStatus(this.translate("no_fitments_available", { make: this.makeName || this.translate("this_make") }), true);
        return;
      }

      this.populateModels();
      this.setStatus(indexStatus.complete ? "" : this.translate("partial_fitment_data"), !indexStatus.complete);
      this.publishSelectionChange();
    }

    get fitments() {
      return Array.isArray(window.vehicleFitmentIndex) ? window.vehicleFitmentIndex : [];
    }

    get makeFitments() {
      return this.fitments.filter((fitment) => {
        return (
          fitment.make?.gid === this.makeGid ||
          fitment.make?.id === this.makeId ||
          fitment.make?.name === this.makeName
        );
      });
    }

    whenFitmentIndexReady() {
      if (window.vehicleFitmentIndexReady?.then) {
        return window.vehicleFitmentIndexReady;
      }

      if (Array.isArray(window.vehicleFitmentIndex)) {
        return Promise.resolve(window.vehicleFitmentIndex);
      }

      return new Promise((resolve) => {
        window.addEventListener("vehicle-fitment-index:ready", () => resolve(window.vehicleFitmentIndex || []), {
          once: true,
        });
      });
    }

    getSelect(label) {
      return this.selects.find((select) => select.dataset.label === label);
    }

    getSelectedValue(label) {
      return this.getSelect(label)?.value || "";
    }

    getSelectedLabel(label) {
      const option = this.getSelect(label)?.selectedOptions?.[0];
      return option?.value ? option.textContent.trim() : "";
    }

    onSelectChange(event) {
      const label = event.target.dataset.label;

      if (label === "Model") {
        this.populateYears();
      }

      if (label === "Year") {
        this.populateEngines();
      }

      if (label === "Engine") {
        this.populateCards();
      }

      this.publishSelectionChange();
    }

    populateModels() {
      const modelSelect = this.getSelect("Model");
      const options = this.uniqueOptions(this.makeFitments, (fitment) => ({
        id: fitment.model?.id,
        value: fitment.model?.gid,
        label: fitment.model?.name,
      }));

      this.replaceOptions(modelSelect, options);
      this.enableSelect(modelSelect, options.length > 0);
      this.resetAfter("Model");
      this.clearCards();
    }

    populateYears() {
      const yearSelect = this.getSelect("Year");
      const modelGid = this.getSelectedValue("Model");
      const matchingFitments = this.makeFitments.filter((fitment) => fitment.model?.gid === modelGid);
      const yearOptions = [];

      matchingFitments.forEach((fitment) => {
        const year = fitment.year;
        yearOptions.push({
          id: year.id,
          value: year.gid,
          label: year.name,
          number: Number(year.number || year.name),
        });
      });

      const options = this.uniqueOptions(yearOptions, (year) => year, (left, right) => left.number - right.number);
      this.replaceOptions(yearSelect, options);
      this.enableSelect(yearSelect, options.length > 0);
      this.resetAfter("Year");
      this.clearCards();
    }

    populateEngines() {
      const engineSelect = this.getSelect("Engine");
      const modelGid = this.getSelectedValue("Model");
      const yearGid = this.getSelectedValue("Year");
      const matchingFitments = this.makeFitments.filter((fitment) => {
        return fitment.model?.gid === modelGid && fitment.year?.gid === yearGid;
      });
      const options = this.uniqueOptions(matchingFitments, (fitment) => {
        const engine = this.normalizedEngine(fitment);
        return {
          id: engine.id,
          value: engine.value,
          label: engine.name,
        };
      });

      this.replaceOptions(engineSelect, options);
      this.enableSelect(engineSelect, options.length > 0);

      if (options.length === 1) {
        engineSelect.value = options[0].value;
        this.populateCards();
      } else {
        this.clearCards();
      }
    }

    populateCards() {
      const modelGid = this.getSelectedValue("Model");
      const yearGid = this.getSelectedValue("Year");
      const engineValue = this.getSelectedValue("Engine");

      if (!modelGid || !yearGid) {
        this.clearCards();
        return;
      }

      const matchingFitments = this.makeFitments.filter((fitment) => {
        return (
          fitment.model?.gid === modelGid &&
          fitment.year?.gid === yearGid &&
          (!engineValue || this.normalizedEngine(fitment).value === engineValue)
        );
      });

      this.renderCards(matchingFitments);
    }

    renderCards(fitments) {
      if (!this.results || !this.cardList) return;

      this.clearSelectedFitment(false);
      this.cardList.innerHTML = "";
      this.results.hidden = false;
      if (fitments.length) {
        this.updateSelectedVehicleSummary();
      } else {
        this.resultHeading.textContent = this.translate("no_exact_fitment");
      }

      fitments
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true }))
        .forEach((fitment) => this.cardList.append(this.createFitmentCard(fitment)));

      this.cardList.append(this.createRequestCard());
      this.syncFitmentCardSelection();
      this.renderActionDock(fitments.length > 0);
    }

    updateSelectedVehicleSummary() {
      if (!this.resultHeading) {
        return;
      }

      const selection = ["Year", "Model", "Engine"]
        .map((label) => this.getSelectedLabel(label))
        .filter((label) => label && label !== this.translate("any"));
      const vehicleDescription = [selection[0], this.makeName, ...selection.slice(1)]
        .filter(Boolean)
        .join(" ");

      if (!vehicleDescription) {
        this.resultHeading.textContent = this.translate("select_vehicle_configuration");
        return;
      }

      const summary = document.createElement("strong");
      summary.className = "fitment-card-results-summary";
      summary.textContent = vehicleDescription;
      this.resultHeading.replaceChildren(this.translate("select_current_prefix"), summary, this.translate("configuration_below"));
    }

    clearCards() {
      if (!this.results || !this.cardList) return;
      this.clearSelectedFitment(false);
      this.cardList.innerHTML = "";
      this.results.hidden = true;
      this.renderActionDock(false);
    }

    createFitmentCard(fitment) {
      const card = document.createElement("div");
      card.className = "fitment-card js-fitment-card";
      card.dataset.fitmentId = fitment.id;
      card.dataset.fitmentGid = fitment.gid;
      card.setAttribute("role", "radio");
      card.setAttribute("aria-checked", "false");
      card.tabIndex = -1;
      card.addEventListener("click", () => this.selectFitmentCard(card, fitment));
      card.addEventListener("keydown", (event) => this.onFitmentCardKeydown(event));

      const image = fitment.imageUrl
        ? `<img src="${this.escapeAttribute(fitment.imageUrl)}" alt="" loading="lazy">`
        : '<span class="fitment-card-placeholder" aria-hidden="true"></span>';
      const headline = String(fitment.headline || fitment.name || "").trim();
      const notesText = String(fitment.notes || "").trim();
      const helperText = this.extractHelperText(fitment.helperText);
      const details = [
        [this.translate("trim"), fitment.trim, "fitment-card-detail--primary"],
        [this.translate("existing_lift"), fitment.existingLift],
        [this.translate("generation"), fitment.generation],
        [this.translate("body"), fitment.body],
      ].map(([label, value, modifier]) => {
        const text = String(value || "").trim();
        return text
          ? `<span class="fitment-card-detail${modifier ? ` ${modifier}` : ""}"><span class="fitment-card-detail-label">${this.escapeHtml(label)}</span><span class="fitment-card-detail-value">${this.escapeHtml(text)}</span></span>`
          : "";
      }).join("");
      const hasStructuredGuidance = Boolean(fitment.headline || details);
      const isDefaultNote = notesText === "Shop compatible kits for this vehicle." || notesText === this.translate("fallback_note");
      const notes = notesText && !isDefaultNote
        ? `<span class="fitment-card-caption">${this.escapeHtml(notesText)}</span>`
        : !hasStructuredGuidance
          ? `<span class="fitment-card-caption">${this.escapeHtml(this.translate("fallback_note"))}</span>`
          : "";

      card.innerHTML = `
        <span class="fitment-card-media">${image}</span>
        <span class="fitment-card-body">
          <strong class="fitment-card-headline">${this.escapeHtml(headline)}</strong>
          ${details}
          ${notes}
        </span>
      `;

      const option = document.createElement("div");
      option.className = "fitment-card-option";
      option.append(card);

      if (!helperText) {
        return option;
      }

      const helperTarget = headline || this.translate("this_option");
      const moreInformationLabel = this.translate("more_information", { option: helperTarget });
      const hideInformationLabel = this.translate("hide_information", { option: helperTarget });
      const frame = document.createElement("div");
      frame.className = "fitment-card-frame";
      card.classList.add("fitment-card-choice");

      const helperToggle = document.createElement("button");
      helperToggle.type = "button";
      helperToggle.className = "fitment-card-helper-toggle";
      helperToggle.setAttribute("aria-expanded", "false");
      helperToggle.setAttribute("aria-label", moreInformationLabel);
      helperToggle.textContent = "?";

      const helper = document.createElement("div");
      helper.className = "fitment-card-helper";
      helper.hidden = true;
      helper.setAttribute("role", "region");
      helper.setAttribute("aria-label", this.translate("information_about", { option: helperTarget }));
      helper.textContent = helperText;

      helperToggle.addEventListener("click", () => {
        this.selectFitmentCard(card, fitment);
        const isExpanded = helperToggle.getAttribute("aria-expanded") === "true";
        helperToggle.setAttribute("aria-expanded", String(!isExpanded));
        helperToggle.setAttribute("aria-label", isExpanded ? moreInformationLabel : hideInformationLabel);
        helper.hidden = isExpanded;
      });

      frame.append(card, helperToggle, helper);
      option.replaceChildren(frame);
      return option;
    }

    renderActionDock(show) {
      this.ensureActionDock();

      if (!this.actionDock || !this.actionDockStatus || !this.actionDockActions) {
        return;
      }

      this.actionDock.hidden = !show;
      this.actionDockActions.innerHTML = "";

      if (!show) {
        this.actionDockStatus.textContent = "";
        this.actionDockActions.hidden = true;
        return;
      }

      if (!this.selectedFitment) {
        this.actionDockStatus.textContent = this.actionDockPrompt;
        this.actionDockActions.hidden = true;
        return;
      }

      this.actionDockStatus.textContent = this.translate("selected_vehicle", { vehicle: this.selectedFitment.name });
      this.actionDockActions.hidden = false;
      this.actionDockActions.append(
        this.createActiveFitmentAction(this.selectedFitment, false),
        this.createViewKitsAction(this.selectedFitment, true),
      );
    }

    createViewKitsAction(fitment, isPrimary) {
      const link = document.createElement("a");
      link.className = this.actionClassName(isPrimary);
      link.href = this.buildCollectionUrl(fitment.gid);
      link.textContent = this.viewKitsActionLabel;
      link.addEventListener("click", () => this.storePendingSelection(fitment));
      return link;
    }

    createActiveFitmentAction(fitment, isPrimary) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = this.actionClassName(isPrimary);
      button.textContent = this.activeFitmentActionLabel;
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.setActiveFitment(fitment, button);
      });
      return button;
    }

    actionClassName(isPrimary) {
      return `fitment-dock-action ${isPrimary ? "fitment-dock-action--primary" : "fitment-dock-action--secondary"}`;
    }

    async setActiveFitment(fitment, button) {
      button.disabled = true;

      try {
        const metadata = {
          displayName: fitment.name,
          imageUrl: fitment.imageUrl || fitment.image || "",
          selectionPath: this.selectionPathForFitment(fitment),
        };

        if (window.vehicleFitmentController?.setCurrentVehicle) {
          await window.vehicleFitmentController.setCurrentVehicle(fitment.id, metadata);
        } else {
          const timestamp = new Date().toISOString();
          window.ThemeStorage?.setJson?.("vehicle_fitment_history", {
            current: {
              id: String(fitment.id),
              firstSeen: timestamp,
              lastSeen: timestamp,
              ...metadata,
            },
            history: [],
          });
          window.refreshFitmentFormWraps?.();
        }
      } finally {
        button.disabled = false;
      }
    }

    createRequestCard() {
      const link = document.createElement("a");
      link.className = "fitment-card fitment-card-request js-fitment-request-card";
      link.href = this.buildVehicleRequestUrl();
      link.innerHTML = `
        <span class="fitment-card-body">
          <strong>${this.escapeHtml(this.translate("request_heading"))}</strong>
          <span class="fitment-card-caption">${this.escapeHtml(this.translate("request_body"))}</span>
        </span>
      `;
      return link;
    }

    selectFitmentCard(card, fitment) {
      this.selectedFitment = fitment;
      this.syncFitmentCardSelection(card);
      this.renderActionDock(true);
      this.publishSelectionChange();
    }

    publishSelectionChange() {
      const model = this.selectedOption("Model");
      const year = this.selectedOption("Year");
      const engine = this.selectedOption("Engine");
      const matchingFitments = this.fitmentsForSelection({ model, year, engine });

      document.dispatchEvent(new CustomEvent(this.selectionEvent, {
        detail: {
          make: {
            id: this.makeId,
            gid: this.makeGid,
            name: this.makeName,
          },
          model,
          year,
          engine,
          fitmentGids: matchingFitments.map((fitment) => fitment.gid).filter(Boolean),
          exactFitmentGid: this.selectedFitment?.gid || "",
          partial: !window.vehicleFitmentIndexStatus?.complete,
        },
      }));
    }

    selectedOption(label) {
      const select = this.getSelect(label);
      const option = select?.selectedOptions?.[0];

      if (!option?.value) return null;

      return {
        id: option.dataset.id || "",
        gid: label === "Engine"
          ? (option.dataset.id ? `gid://shopify/Metaobject/${option.dataset.id}` : "")
          : option.value,
        name: option.textContent.trim(),
        value: option.value,
      };
    }

    fitmentsForSelection({ model, year, engine }) {
      return this.makeFitments.filter((fitment) => {
        if (model?.gid && fitment.model?.gid !== model.gid) return false;
        if (year?.gid && fitment.year?.gid !== year.gid) return false;
        if (engine?.value && this.normalizedEngine(fitment).value !== engine.value) return false;
        return true;
      });
    }

    onFitmentCardKeydown(event) {
      const navigationKeys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End"];
      if (!navigationKeys.includes(event.key)) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.currentTarget.click();
        }
        return;
      }

      const cards = Array.from(this.cardList?.querySelectorAll(".js-fitment-card[data-fitment-id]") || []);
      if (!cards.length) {
        return;
      }

      const currentIndex = cards.indexOf(event.currentTarget);
      if (currentIndex < 0) {
        return;
      }

      event.preventDefault();
      let nextIndex = currentIndex;

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % cards.length;
      } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + cards.length) % cards.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = cards.length - 1;
      }

      cards[nextIndex].focus();
      cards[nextIndex].click();
    }

    clearSelectedFitment(renderDock = true) {
      this.selectedFitment = null;
      this.syncFitmentCardSelection();

      if (renderDock) {
        this.renderActionDock(this.hasSupportedFitmentCards());
      }
    }

    syncFitmentCardSelection(selectedCard = null) {
      const cards = Array.from(this.cardList?.querySelectorAll(".js-fitment-card[data-fitment-id]") || []);
      const selectedId = this.selectedFitment ? String(this.selectedFitment.id) : "";

      cards.forEach((card, index) => {
        const isSelected = selectedCard ? card === selectedCard : Boolean(selectedId && card.dataset.fitmentId === selectedId);
        card.classList.toggle("is-selected", isSelected);
        card.closest(".fitment-card-frame")?.classList.toggle("is-selected", isSelected);
        card.setAttribute("aria-checked", isSelected ? "true" : "false");
        card.tabIndex = isSelected || (!selectedId && index === 0) ? 0 : -1;
      });
    }

    hasSupportedFitmentCards() {
      return Boolean(this.cardList?.querySelector(".js-fitment-card[data-fitment-id]"));
    }

    buildCollectionUrl(fitmentGid) {
      if (window.VehicleFitmentUrls?.buildFitmentCollectionUrl) {
        return window.VehicleFitmentUrls.buildFitmentCollectionUrl(fitmentGid);
      }

      const baseUrl = window.routes?.all_products_url || "/collections/all";
      const url = new URL(baseUrl, window.requestShopUrl || window.location.origin);
      url.searchParams.set(this.fitmentParam, fitmentGid);
      return url.toString();
    }

    buildVehicleRequestUrl() {
      const url = new URL("/pages/vehicle-request", window.requestShopUrl || window.location.origin);

      [
        ["make", this.makeName],
        ["model", this.getSelectedLabel("Model")],
        ["year", this.getSelectedLabel("Year")],
        ["engine", this.getSelectedLabel("Engine")],
      ].forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        }
      });

      url.searchParams.set("source", "vehicle_make_explorer");
      return url.toString();
    }

    storePendingSelection(fitment) {
      window.ThemeStorage?.setJson?.(
        this.pendingSelectionKey,
        this.selectionPathForFitment(fitment),
        "sessionStorage",
      );
    }

    selectionPathForFitment(fitment) {
      return {
        fitmentId: fitment.id,
        makeGid: fitment.make?.gid || this.makeGid,
        modelGid: fitment.model?.gid || this.getSelectedValue("Model"),
        yearGid: this.getSelectedValue("Year") || fitment.year?.gid || "",
        engineValue: this.getSelectedValue("Engine") || this.normalizedEngine(fitment).value,
      };
    }

    normalizedEngine(fitment) {
      const sourceName = fitment.engine?.name || "Any";
      const name = sourceName === "Any" ? this.translate("any") : sourceName;
      return {
        id: fitment.engine?.id || "",
        gid: fitment.engine?.gid || "",
        name,
        value: String(fitment.engine?.value || sourceName).trim().toLowerCase(),
      };
    }

    uniqueOptions(items, getOption, sortFn) {
      const byValue = new Map();
      items.forEach((item) => {
        const option = getOption(item);
        if (option?.value && option?.label && !byValue.has(option.value)) {
          byValue.set(option.value, option);
        }
      });

      const options = Array.from(byValue.values());
      return sortFn ? options.sort(sortFn) : options.sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }));
    }

    replaceOptions(select, options) {
      if (!select) return;
      this.selectRemoveOptions(select);
      options.forEach((option) => {
        const element = document.createElement("option");
        element.value = option.value;
        element.textContent = option.label;
        if (option.id) element.dataset.id = option.id;
        select.append(element);
      });
    }

    resetAfter(label) {
      const labels = ["Model", "Year", "Engine"];
      const index = labels.indexOf(label);
      labels.slice(index + 1).forEach((nextLabel) => {
        const select = this.getSelect(nextLabel);
        this.selectRemoveOptions(select);
        this.enableSelect(select, false);
      });
    }

    enableSelect(select, enabled) {
      if (!select) return;
      select.disabled = !enabled;
      select.closest(".form-group")?.classList.toggle("disabled", !enabled);
    }

    selectRemoveOptions(select) {
      if (!select) return;
      select.querySelectorAll("option:not(.default)").forEach((option) => option.remove());
      const defaultOption = select.querySelector("option.default");
      if (defaultOption) defaultOption.selected = true;
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.classList.toggle("fitment-explorer-status-error", isError);
    }

    escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (char) => {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#039;",
        }[char];
      });
    }

    extractHelperText(value) {
      try {
        const collect = (node) => {
          if (!node || typeof node !== "object") return [];
          if (node.type === "text" && typeof node.value === "string") return [node.value];
          return Array.isArray(node.children) ? node.children.flatMap(collect) : [];
        };
        return collect(JSON.parse(String(value || ""))).join(" ").replace(/\s+/g, " ").trim();
      } catch {
        return "";
      }
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  customElements.define("vehicle-make-fitment-explorer", VehicleMakeFitmentExplorer);
}
